import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { LOG_API, type Log } from '@chess/api-log';
import { MISTAKE_RULES_API, type MistakeRules } from '@chess/api-model';
import { MistakeRulesService } from './rules.ts';
import { MistakeRulesForm, type SaveStatus } from './MistakeRulesForm.tsx';
import './settings-mistakes.css';

// The blunder and miss rules: serves them as the `mistakeRules` API (read
// from `settings/mistakes.json`, the defaults until then) and adds their
// section to the settings screen. Every reader of the scan's verdicts (the
// game store's facts, the review) follows the API and re-reads on a change.
export function setup(app: PiletApi) {
  const log = app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name));
  const storage = app.getApi<Storage>(STORAGE_API).then((s) => s.scope('settings'));
  const service = new MistakeRulesService(storage, (message, fields) => void log.then((l) => l.warn(message, fields as never)));
  app.registerApi(MISTAKE_RULES_API, service);

  const Section: React.FC = () => {
    const [rules, setRules] = React.useState<MistakeRules | undefined>();
    const [status, setStatus] = React.useState<SaveStatus>('loading');
    const [error, setError] = React.useState<string | undefined>();
    React.useEffect(() => {
      let live = true;
      void service.ready().then((r) => {
        if (!live) return;
        setRules(r);
        setStatus('idle');
      });
      const stop = service.subscribe((r) => live && setRules(r));
      return () => {
        live = false;
        stop();
      };
    }, []);
    const save = (next: MistakeRules) => {
      setStatus('saving');
      setError(undefined);
      service.save(next).then(
        (saved) => {
          setRules(saved);
          setStatus('saved');
        },
        (e) => {
          setStatus('error');
          setError(e instanceof Error ? e.message : String(e));
        },
      );
    };
    return <MistakeRulesForm rules={rules} status={status} error={error} onSave={save} />;
  };

  app.registerExtension('settings.sections', Section, { order: 20 });
}
